import React from "react";
import {
  AbsoluteFill,
  Audio,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
  Easing,
} from "remotion";

export const NixlevFrictionlessOpsi2: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Premium Architectural Palette (Fresh Slate & Signature Nixlev Emerald)
  const bgMain = "#F8FAFC";
  const bgCard = "#FFFFFF";
  const textDark = "#0F172A";
  const textMuted = "#64748B";
  const emeraldPrimary = "#059669";
  const emeraldLight = "#ECFDF5";
  const emeraldBorder = "#10B981";
  const redAlert = "#EF4444";
  const redSoft = "#FEF2F2";
  const goldAccent = "#D97706";

  // Subtitle Timestamps (945 frames total = 31.5s @ 30fps) - NO EMOJIS IN SUBTITLES
  const SUBTITLES = [
    { from: 0, to: 165, text: "Pembeli zaman sekarang paling malas kalau mau beli e-book harus nunggu admin balas chat.", highlight: "paling malas" },
    { from: 165, to: 345, text: "Nanya nomor rekening, transfer, kirim bukti transfer... keburu batal beli!", highlight: "keburu batal beli!" },
    { from: 345, to: 495, text: "Pakai Nixlev, alur belanja cuma butuh tiga puluh detik.", highlight: "cuma butuh 30 detik" },
    { from: 495, to: 725, text: "Pembeli klik link tokomu, scan QRIS, file langsung meluncur ke WhatsApp detik itu juga.", highlight: "langsung meluncur ke WhatsApp" },
    { from: 725, to: 855, text: "Toko melayani otomatis, pembeli senang karena serba instan.", highlight: "serba instan" },
    { from: 855, to: 945, text: "Bikin tokomu gratis hari ini di nixlev.com.", highlight: "nixlev.com" },
  ];

  const currentSub = SUBTITLES.find((s) => frame >= s.from && frame < s.to);

  // Springs Helper
  const sp = (delay: number, damping = 15.35, stiffness = 115) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 1.0 },
    });

  // Micro-motion breathing float (Subtle alive motion)
  const floatY = Math.sin(frame / 22) * 4;

  // ==========================================
  // PHASE 1 ANIMATIONS (Chat Drama: Frame 0 - 345)
  // ==========================================
  const p1CardSpring = sp(0, 16, 120);
  const p1CardScale = interpolate(p1CardSpring, [0, 1], [0.92, 1]);
  const p1CardY = interpolate(p1CardSpring, [0, 1], [60, 0]);
  const p1CardOpacity = interpolate(p1CardSpring, [0, 1], [0, 1]);

  // Chat Bubble 1 Entry
  const bubble1Spring = sp(15, 14, 130);
  const bubble1Y = interpolate(bubble1Spring, [0, 1], [30, 0]);
  const bubble1Scale = interpolate(bubble1Spring, [0, 1], [0.9, 1]);
  const bubble1Opacity = interpolate(bubble1Spring, [0, 1], [0, 1]);

  // Delay Indicator Entry
  const delaySpring = sp(60, 14, 130);
  const delayY = interpolate(delaySpring, [0, 1], [25, 0]);
  const delayOpacity = interpolate(delaySpring, [0, 1], [0, 1]);

  // Friction Chain Tags (Staggered Entrance starting at frame 120 so all 4 tags are fully settled before voice speaks)
  const tag1Spring = sp(115, 14, 130);
  const tag2Spring = sp(125, 14, 130);
  const tag3Spring = sp(135, 14, 130);
  const tag4Spring = sp(145, 14, 130);

  // Phase 1 Exit Transition (Frame 335 - 355) - Fluid Glide Up
  const p1Exit = interpolate(
    frame,
    [335, 355],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const p1ExitY = interpolate(p1Exit, [0, 1], [0, -70]);
  const p1ExitScale = interpolate(p1Exit, [0, 1], [1, 0.88]);
  const p1ExitOpacity = interpolate(p1Exit, [0, 0.7], [1, 0]);

  // ==========================================
  // PHASE 2 ANIMATIONS (Nixlev 3-Step: Frame 345 - 730)
  // ==========================================
  const p2Entry = interpolate(
    frame,
    [345, 375],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const p2EntryY = interpolate(p2Entry, [0, 1], [70, 0]);
  const p2EntryScale = interpolate(p2Entry, [0, 1], [0.9, 1]);
  const p2EntryOpacity = interpolate(p2Entry, [0, 0.5], [0, 1]);

  // Staggered Step Cards:
  // Step 1: Open Bio Link (Frame 360)
  const s1Spring = sp(360, 14, 130);
  const s1Y = interpolate(s1Spring, [0, 1], [30, 0]);
  const s1Scale = interpolate(s1Spring, [0, 1], [0.95, 1]);
  const s1Opacity = interpolate(s1Spring, [0, 1], [0, 1]);

  // Step 2: Instant QRIS (Frame 460)
  const s2Spring = sp(460, 14, 130);
  const s2Y = interpolate(s2Spring, [0, 1], [35, 0]);
  const s2Scale = interpolate(s2Spring, [0, 1], [0.93, 1]);
  const s2Opacity = interpolate(s2Spring, [0, 1], [0, 1]);
  const scanLaserY = Math.sin(frame / 6) * 32;

  // Step 3: Direct WhatsApp Dispatch (Frame 550)
  const s3Spring = sp(550, 13, 140);
  const s3Y = interpolate(s3Spring, [0, 1], [40, 0]);
  const s3Scale = interpolate(s3Spring, [0, 1], [0.92, 1]);
  const s3Opacity = interpolate(s3Spring, [0, 1], [0, 1]);

  // Phase 2 Exit Transition (Frame 715 - 740)
  const p2Exit = interpolate(
    frame,
    [715, 740],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const p2ExitY = interpolate(p2Exit, [0, 1], [0, -70]);
  const p2ExitScale = interpolate(p2Exit, [0, 1], [1, 0.88]);
  const p2ExitOpacity = interpolate(p2Exit, [0, 0.7], [1, 0]);

  // ==========================================
  // PHASE 3 ANIMATIONS (Outro & Hero Metric: Frame 730 - 945)
  // ==========================================
  const p3Entry = interpolate(
    frame,
    [730, 765],
    [0, 1],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.bezier(0.16, 1, 0.3, 1) }
  );
  const p3EntryY = interpolate(p3Entry, [0, 1], [70, 0]);
  const p3EntryScale = interpolate(p3Entry, [0, 1], [0.88, 1]);
  const p3EntryOpacity = interpolate(p3Entry, [0, 0.5], [0, 1]);

  const ctaPulse = Math.sin(frame / 10) * 3;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: bgMain,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
        color: textDark,
        overflow: "hidden",
      }}
    >
      {/* Background Precision Grid Lines */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `
            linear-gradient(to right, rgba(100, 116, 139, 0.04) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(100, 116, 139, 0.04) 1px, transparent 1px)
          `,
          backgroundSize: "48px 48px",
          pointerEvents: "none",
        }}
      />

      {/* Audio Layer */}
      <Audio src={staticFile("nixlev_opsi2_frictionless.mp3")} volume={1.0} />
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.12} />

      {/* Frame-Accurate Micro-SFX */}
      {frame === 15 && <Audio src={staticFile("pop.mp3")} volume={0.6} />}
      {frame === 60 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.5} />}
      {frame === 150 && <Audio src={staticFile("ui-pop.mp3")} volume={0.5} />}
      {frame === 204 && <Audio src={staticFile("click-soft.mp3")} volume={0.6} />}
      {frame === 345 && <Audio src={staticFile("whoosh.mp3")} volume={0.7} />}
      {frame === 360 && <Audio src={staticFile("swoosh-quick.mp3")} volume={0.5} />}
      {frame === 460 && <Audio src={staticFile("click.mp3")} volume={0.6} />}
      {frame === 550 && <Audio src={staticFile("ui-success.mp3")} volume={0.85} />}
      {frame === 730 && <Audio src={staticFile("chime.mp3")} volume={0.9} />}

      {/* ========================================================================= */}
      {/* MASTER CONTENT CONTAINER: DEAD-CENTERED HORIZONTALLY & VERTICALLY         */}
      {/* (top: 48%, left: 50%, transform: translate(-50%, -50%))                   */}
      {/* ========================================================================= */}
      <div
        style={{
          position: "absolute",
          top: "48%", // Optical dead-center of screen
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 900,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* ==================================================== */}
        {/* CHAPTER 1: THE MANUAL CHAT FRICTION (Frame 0 - 345) */}
        {/* ==================================================== */}
        {frame < 355 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${p1CardY + p1ExitY + floatY}px) scale(${p1CardScale * p1ExitScale})`,
              opacity: p1CardOpacity * p1ExitOpacity,
            }}
          >
            {/* Warning Tag */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: redSoft,
                border: `1.5px solid ${redAlert}`,
                color: redAlert,
                padding: "10px 24px",
                borderRadius: 30,
                fontSize: 18,
                fontWeight: 800,
                marginBottom: 24,
                boxShadow: "0 4px 15px rgba(239, 68, 68, 0.08)",
              }}
            >
              {/* Alert Vector Icon */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={redAlert} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>CARA LAMA: RIBET & BANYAK DRAMA</span>
            </div>

            <h1
              style={{
                fontSize: 54,
                fontWeight: 900,
                lineHeight: 1.15,
                textAlign: "center",
                letterSpacing: "-0.03em",
                marginBottom: 35,
              }}
            >
              Mau Beli E-Book, <br />
              <span style={{ color: redAlert }}>Tapi Harus Nunggu Admin Balas?</span>
            </h1>

            {/* Chat Simulation Card */}
            <div
              style={{
                width: 840,
                backgroundColor: bgCard,
                borderRadius: 32,
                border: "2px solid #E2E8F0",
                padding: 36,
                boxShadow: "0 25px 60px rgba(0,0,0,0.06)",
                display: "flex",
                flexDirection: "column",
                gap: 20,
              }}
            >
              {/* Customer Chat Bubble */}
              <div
                style={{
                  alignSelf: "flex-end",
                  backgroundColor: "#E2F7CB",
                  color: textDark,
                  padding: "18px 26px",
                  borderRadius: "24px 24px 4px 24px",
                  fontSize: 21,
                  fontWeight: 700,
                  maxWidth: 620,
                  transform: `translateY(${bubble1Y}px) scale(${bubble1Scale})`,
                  opacity: bubble1Opacity,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
                }}
              >
                <div>"Halo kak, mau beli E-Book Creator. Minta nomor rekeningnya ya..."</div>
                <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, fontSize: 13, color: "#64748B", marginTop: 8 }}>
                  <span>14:20</span>
                  {/* Vector Double Check Icon (Zero Emoji) */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 6 7 17l-5-5"/>
                    <path d="m22 10-7.5 7.5L13 16"/>
                  </svg>
                </div>
              </div>

              {/* Friction Delay Status */}
              <div
                style={{
                  alignSelf: "center",
                  backgroundColor: "#F1F5F9",
                  padding: "12px 26px",
                  borderRadius: 20,
                  fontSize: 16,
                  color: "#64748B",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  transform: `translateY(${delayY}px)`,
                  opacity: delayOpacity,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span>Admin offline • Menunggu balasan 3 jam...</span>
              </div>

              {/* Friction Chain Tags with Pure Vector SVGs (NO BROKEN ICONS/NO TOFU) */}
              <div
                style={{
                  marginTop: 15,
                  padding: "22px 24px",
                  backgroundColor: redSoft,
                  border: "1.5px dashed #FCA5A5",
                  borderRadius: 22,
                  display: "flex",
                  justifyContent: "space-around",
                  alignItems: "center",
                }}
              >
                {/* Tag 1: Nanya No Rek */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                    transform: `scale(${interpolate(tag1Spring, [0, 1], [0.75, 1])})`,
                    opacity: interpolate(tag1Spring, [0, 1], [0, 1]),
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                    </svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#991B1B" }}>Nanya No Rek</div>
                </div>

                {/* Arrow 1 */}
                <div style={{ opacity: interpolate(tag2Spring, [0, 1], [0, 1]) }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>

                {/* Tag 2: Kirim Bukti Transfer */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                    transform: `scale(${interpolate(tag2Spring, [0, 1], [0.75, 1])})`,
                    opacity: interpolate(tag2Spring, [0, 1], [0, 1]),
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                    </svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#991B1B" }}>Kirim Bukti TF</div>
                </div>

                {/* Arrow 2 */}
                <div style={{ opacity: interpolate(tag3Spring, [0, 1], [0, 1]) }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>

                {/* Tag 3: Tunggu Dicek */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                    transform: `scale(${interpolate(tag3Spring, [0, 1], [0.75, 1])})`,
                    opacity: interpolate(tag3Spring, [0, 1], [0, 1]),
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "#FEE2E2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <polyline points="12 6 12 12 16 14"></polyline>
                    </svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#991B1B" }}>Tunggu Dicek</div>
                </div>

                {/* Arrow 3 */}
                <div style={{ opacity: interpolate(tag4Spring, [0, 1], [0, 1]) }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </div>

                {/* Tag 4: BATAL BELI! */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 6,
                    transform: `scale(${interpolate(tag4Spring, [0, 1], [0.75, 1])})`,
                    opacity: interpolate(tag4Spring, [0, 1], [0, 1]),
                  }}
                >
                  <div style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "#DC2626", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 900, color: "#DC2626" }}>BATAL BELI!</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* CHAPTER 2: NIXLEV 30-SECOND STREAMLINE (Frame 345 - 730) */}
        {/* ==================================================== */}
        {frame >= 340 && frame < 735 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${p2EntryY + p2ExitY + floatY}px) scale(${p2EntryScale * p2ExitScale})`,
              opacity: p2EntryOpacity * p2ExitOpacity,
            }}
          >
            {/* Solution Header Badge */}
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 10,
                backgroundColor: emeraldLight,
                border: `2px solid ${emeraldBorder}`,
                color: emeraldPrimary,
                padding: "10px 24px",
                borderRadius: 30,
                fontSize: 18,
                fontWeight: 900,
                marginBottom: 20,
                boxShadow: "0 6px 20px rgba(16, 185, 129, 0.15)",
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={emeraldPrimary} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>CARA NIXLEV: SELESAI DALAM 30 DETIK</span>
            </div>

            <h1
              style={{
                fontSize: 54,
                fontWeight: 900,
                lineHeight: 1.15,
                textAlign: "center",
                letterSpacing: "-0.03em",
                marginBottom: 25,
              }}
            >
              Alur Belanja Otomatis, <br />
              <span style={{ color: emeraldPrimary }}>Tanpa Sentuh Chat Manual.</span>
            </h1>

            {/* 3-Step Connected Bento Cards */}
            <div style={{ width: 840, display: "flex", flexDirection: "column", gap: 18 }}>
              {/* Step 1: Open Bio Link */}
              <div
                style={{
                  backgroundColor: bgCard,
                  borderRadius: 24,
                  border: "1.5px solid #E2E8F0",
                  padding: "20px 28px",
                  display: "flex",
                  alignItems: "center",
                  gap: 20,
                  boxShadow: "0 10px 25px rgba(0,0,0,0.04)",
                  transform: `translateY(${s1Y}px) scale(${s1Scale})`,
                  opacity: s1Opacity,
                }}
              >
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 16,
                    backgroundColor: emeraldLight,
                    color: emeraldPrimary,
                    fontSize: 22,
                    fontWeight: 900,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1.5px solid #A7F3D0`,
                  }}
                >
                  1
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 19, fontWeight: 900, color: textDark }}>Pembeli Buka Link Tokomu</div>
                  <div style={{ fontSize: 14, color: textMuted, marginTop: 3 }}>Pilih e-book atau template dalam etalase yang rapi</div>
                </div>
                <div style={{ backgroundColor: "#F1F5F9", padding: "6px 16px", borderRadius: 10, fontSize: 14, fontWeight: 700, color: textDark }}>
                  nixlev.com/@kreator
                </div>
              </div>

              {/* Step 2: Instant QRIS Scan */}
              <div
                style={{
                  backgroundColor: bgCard,
                  borderRadius: 24,
                  border: `2px solid ${emeraldBorder}`,
                  padding: "22px 28px",
                  display: "flex",
                  alignItems: "center",
                  gap: 20,
                  boxShadow: "0 15px 35px rgba(16, 185, 129, 0.12)",
                  transform: `translateY(${s2Y}px) scale(${s2Scale})`,
                  opacity: s2Opacity,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 16,
                    backgroundColor: emeraldLight,
                    color: emeraldPrimary,
                    fontSize: 22,
                    fontWeight: 900,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `1.5px solid #A7F3D0`,
                  }}
                >
                  2
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 19, fontWeight: 900, color: "#065F46" }}>Checkout Scan QRIS 1-Klik</div>
                  <div style={{ fontSize: 14, color: textMuted, marginTop: 3 }}>Mendukung semua m-banking & e-wallet tanpa nanya nomor rekening</div>
                </div>
                {/* QRIS Icon Vector Badge */}
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    backgroundColor: "#111827",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    position: "relative",
                  }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5">
                    <rect x="3" y="3" width="7" height="7"></rect>
                    <rect x="14" y="3" width="7" height="7"></rect>
                    <rect x="14" y="14" width="7" height="7"></rect>
                    <rect x="3" y="14" width="7" height="7"></rect>
                  </svg>
                  {/* Scan Laser Beam */}
                  <div
                    style={{
                      position: "absolute",
                      left: 4,
                      right: 4,
                      top: 26 + scanLaserY,
                      height: 3,
                      backgroundColor: "#10B981",
                      boxShadow: "0 0 8px #10B981",
                    }}
                  />
                </div>
              </div>

              {/* Step 3: Instant WhatsApp Delivery */}
              <div
                style={{
                  backgroundColor: bgCard,
                  borderRadius: 24,
                  border: "1.5px solid #E2E8F0",
                  padding: "22px 28px",
                  display: "flex",
                  alignItems: "center",
                  gap: 20,
                  boxShadow: "0 20px 45px rgba(0,0,0,0.06)",
                  transform: `translateY(${s3Y}px) scale(${s3Scale})`,
                  opacity: s3Opacity,
                }}
              >
                <div
                  style={{
                    width: 54,
                    height: 54,
                    borderRadius: 16,
                    backgroundColor: "#DCFCE7",
                    color: "#15803D",
                    fontSize: 22,
                    fontWeight: 900,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1.5px solid #86EFAC",
                  }}
                >
                  3
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 19, fontWeight: 900, color: textDark }}>File Meluncur ke WhatsApp</span>
                    <span style={{ backgroundColor: "#DCFCE7", color: "#166534", fontSize: 13, fontWeight: 800, padding: "3px 10px", borderRadius: 8 }}>
                      Instan
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: textMuted, marginTop: 4 }}>
                    Link unduhan & lisensi resmi langsung diterima pembeli detik itu juga
                  </div>
                </div>
                {/* WhatsApp Chat Success Icon */}
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    backgroundColor: "#22C55E",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* CHAPTER 3: OUTRO & HERO CTA (Frame 730 - 945)        */}
        {/* ==================================================== */}
        {frame >= 720 && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              transform: `translateY(${p3EntryY + floatY}px) scale(${p3EntryScale})`,
              opacity: p3EntryOpacity,
            }}
          >
            {/* Top Brand Logo - NIXLEV EMERALD GREEN THEME */}
            <div
              style={{
                width: 96,
                height: 96,
                borderRadius: 28,
                backgroundColor: "#13251F",
                border: "2.5px solid #10B981",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 20px 45px rgba(16, 185, 129, 0.25)",
                marginBottom: 24,
                overflow: "hidden",
              }}
            >
              <Img
                src={staticFile("nixlev_logo_green.png")}
                style={{ width: 68, height: 68, objectFit: "contain" }}
              />
            </div>

            <h1
              style={{
                fontSize: 56,
                fontWeight: 900,
                lineHeight: 1.15,
                textAlign: "center",
                letterSpacing: "-0.03em",
                marginBottom: 30,
              }}
            >
              Toko Melayani Otomatis, <br />
              <span style={{ color: emeraldPrimary }}>Pembeli Puas Tanpa Drama.</span>
            </h1>

            {/* Benefit Box - Clean Vector Icons (No Emoji) */}
            <div
              style={{
                width: 840,
                backgroundColor: bgCard,
                borderRadius: 28,
                border: "1.5px solid #E2E8F0",
                padding: "26px 36px",
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                boxShadow: "0 20px 45px rgba(0,0,0,0.05)",
                marginBottom: 35,
              }}
            >
              {/* Item 1: 30 Detik */}
              <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 32, fontWeight: 900, color: emeraldPrimary }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={emeraldPrimary} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                  </svg>
                  <span>30 Detik</span>
                </div>
                <div style={{ fontSize: 14, color: textMuted, fontWeight: 700, marginTop: 4 }}>Checkout Instan</div>
              </div>

              <div style={{ width: 1, height: 45, backgroundColor: "#E2E8F0" }} />

              {/* Item 2: 100% Otomatis */}
              <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 32, fontWeight: 900, color: textDark }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={emeraldPrimary} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  <span>100%</span>
                </div>
                <div style={{ fontSize: 14, color: textMuted, fontWeight: 700, marginTop: 4 }}>Kirim File Otomatis</div>
              </div>

              <div style={{ width: 1, height: 45, backgroundColor: "#E2E8F0" }} />

              {/* Item 3: Rp 0 */}
              <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 900, color: goldAccent }}>Rp 0</div>
                <div style={{ fontSize: 14, color: textMuted, fontWeight: 700, marginTop: 4 }}>Tanpa Biaya Sewa</div>
              </div>
            </div>

            {/* CTA Button */}
            <div
              style={{
                backgroundColor: "#13251F",
                color: "#FFFFFF",
                border: `2px solid #10B981`,
                borderRadius: 36,
                padding: "22px 60px",
                fontSize: 27,
                fontWeight: 900,
                display: "flex",
                alignItems: "center",
                gap: 16,
                boxShadow: "0 25px 60px rgba(16, 185, 129, 0.25)",
                transform: `scale(${1 + ctaPulse * 0.01})`,
              }}
            >
              <span>Buka Tokomu Gratis di nixlev.com</span>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* DYNAMIC SUBTITLES: ANCHORED IN LOWER GROUNDED SAFE ZONE (Y = 1680px)     */}
      {/* ========================================================================= */}
      {currentSub && (
        <div
          style={{
            position: "absolute",
            bottom: 240, // Grounded safely right above TikTok platform caption zone
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 100,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.94)",
              backdropFilter: "blur(14px)",
              padding: "16px 36px",
              borderRadius: 24,
              border: "1px solid rgba(255, 255, 255, 0.16)",
              boxShadow: "0 15px 35px rgba(0,0,0,0.35)",
              maxWidth: 840,
              textAlign: "center",
              transform: `scale(${spring({
                frame: frame - currentSub.from,
                fps,
                config: { damping: 14, stiffness: 180 },
              })})`,
            }}
          >
            <span style={{ fontSize: 27, fontWeight: 800, color: "#FFFFFF", lineHeight: 1.35 }}>
              {currentSub.highlight ? (
                <>
                  {currentSub.text.split(currentSub.highlight)[0]}
                  <span style={{ color: "#FCD34D" }}>{currentSub.highlight}</span>
                  {currentSub.text.split(currentSub.highlight)[1]}
                </>
              ) : (
                currentSub.text
              )}
            </span>
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};
